import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, FolderOpen, Fullscreen, Globe, Hand, Loader2, Maximize2, Minimize, Monitor, Moon, Pause, Trash2, Undo2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';
import { useWorkspaceQuery } from '../../hooks/queries';
import { useWorkspaceAction } from '../../hooks/mutations';
import ConfirmModal from '../ConfirmModal';
import { detailToMessage } from '../../lib/apiError';
import BotAvatar, { useBotSpecies } from './BotAvatar';
import Bee from '../bees/Bee';
import { HONEY } from '../bees/beeSvg';
import { busyComputers } from './botActivity';
import WorkspaceFiles from './WorkspaceFiles';

const COLLAPSED_KEY = 'bb.computer.collapsed';
const readCollapsed = () => { try { return localStorage.getItem(COLLAPSED_KEY) === '1'; } catch { return false; } };
const writeCollapsed = (v) => { try { localStorage.setItem(COLLAPSED_KEY, v ? '1' : '0'); } catch { /* private mode */ } };

/** Whether the computer above the chat is folded (remembered), for ComputerView's `collapsed`. */
export const useComputerCollapsed = () => {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const set = (v) => { writeCollapsed(v); setCollapsed(v); };
  return [collapsed, set];
};

/** The bot's latest desktop action, in words (of several in one call, the last). */
const describeAction = (t, action) => {
  if (action?.tool?.startsWith('browser_')) return t(`bots.chat.tools.${action.tool}`);
  const args = action?.args || {};
  const a = (Array.isArray(args.actions) && args.actions[args.actions.length - 1]) || args;
  switch (a.action) {
    case 'open_url': {
      let site = a.url || '';
      try { site = new URL(a.url).hostname; } catch { /* keep as is */ }
      return t('bots.desktop.act_open', { site });
    }
    case 'click': case 'double_click': case 'right_click': return t('bots.desktop.act_click');
    case 'type': return t('bots.desktop.act_type');
    case 'key': return t('bots.desktop.act_key', { key: a.key || '' });
    case 'scroll': return t('bots.desktop.act_scroll');
    case 'wait': return t('bots.desktop.act_wait');
    default: return t('bots.desktop.act_look');
  }
};

/** The live screen, with a pulse where the bot just clicked. */
const SCREEN_HEIGHT = { small: '340px', panel: '70vh', big: '78vh', card: '300px', full: 'calc(100dvh - 4.5rem)' };

const viewOnlyFlag = (url) => ((url || '').includes('view_only=1') ? 'view' : 'control');

export const Screen = ({ ws, action, size, onTakeOver, busy, t }) => {
  const frameRef = useRef(null);
  const [connected, setConnected] = useState(false);
  // The screen (deploy/workspace/screen.html) can switch between watching and control
  // without a new connection, which takes seconds: keys typed then were lost or went to
  // this page. Screens of machines started before that are reloaded as they always were.
  const [switchable, setSwitchable] = useState(false);
  const mine = ws.lease.mine;
  const base = (ws.preview_url || '').replace('&view_only=1', '');
  const loaded = useRef({ base: null, url: null });
  if (loaded.current.base !== base || !switchable) loaded.current = { base, url: ws.preview_url };
  const src = switchable ? loaded.current.url : ws.preview_url;

  useEffect(() => { setSwitchable(false); setConnected(false); }, [base]);
  useEffect(() => {
    const onMsg = (e) => {
      if (!frameRef.current || e.source !== frameRef.current.contentWindow) return;
      if (e.data?.bbScreen === 'connected') { setConnected(true); setSwitchable(!!e.data.control); }
      if (e.data?.bbScreen === 'disconnected') setConnected(false);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);
  // Taking control puts the keyboard on the screen, so typing goes to the computer and
  // not to this page's shortcuts. On phones the screen has its own keyboard button
  // (deploy/workspace/screen.html), named here in the person's language.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || !connected) return;
    if (switchable) {
      frame.contentWindow?.postMessage({ bbScreen: 'control', viewOnly: !mine, focus: mine, keyboardLabel: t('bots.desktop.keyboard') }, '*');
    }
    if (mine) frame.focus();
  }, [mine, connected, switchable, t]);

  const args = action?.args || {};
  const fresh = action && Date.now() - action.at < 1800;
  const pulse = fresh && action.tool === 'computer' && Number.isFinite(args.x) && Number.isFinite(args.y);
  return (
    <div className={`group relative w-full mx-auto rounded-xl overflow-hidden bg-card ring-1 ${mine ? 'ring-2 ring-amber-500' : 'ring-border'}`}
      style={{
        aspectRatio: `${ws.screen.width} / ${ws.screen.height}`,
        // Width follows the height limit, so the screen fills the frame exactly
        // (no letterbox) and click markers land where the bot clicked.
        width: `min(100%, calc(${SCREEN_HEIGHT[size]} * ${ws.screen.width / ws.screen.height}))`,
      }}>
      {/* clipboard-write: what is copied on the computer reaches the person's clipboard (screen.html). */}
      <iframe ref={frameRef} key={`${base}|${viewOnlyFlag(src)}`} src={src} title={t('bots.desktop.computer_title')}
        allow="clipboard-read; clipboard-write"
        className={`absolute inset-0 w-full h-full border-0 ${mine ? '' : 'pointer-events-none'}`} />
      {!connected && (
        <div className="absolute inset-0 flex items-center justify-center gap-2 text-xs text-zinc-400">
          <Loader2 className="w-4 h-4 animate-spin" />{t('bots.desktop.connecting')}
        </div>
      )}
      {pulse && (
        <span key={action.at} className="pointer-events-none absolute w-8 h-8 -ml-4 -mt-4 rounded-full border-2 border-amber-400 animate-ping"
          style={{ left: `${(args.x / ws.screen.width) * 100}%`, top: `${(args.y / ws.screen.height) * 100}%` }} />
      )}
      {!mine && ws.lease.owner === 'bot' && (
        <div className="absolute inset-x-0 bottom-0 flex justify-center pb-3 opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity">
          <Button size="sm" className="bb-always-dark h-8 gap-1.5 bg-black/70 hover:bg-black/85 text-white backdrop-blur border border-white/10"
            disabled={busy} onClick={onTakeOver}>
            <Hand className="w-3.5 h-3.5" />{t('bots.desktop.take_control')}
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * The project's computer, like Grok Bot's "Agent Computer": a live view of
 * what the bot does, with what it is doing now, and taking control for the
 * steps only a person can do (logins, 2FA, captchas).
 *
 * layout "inline" sits above the chat and folds away; "panel" fills a side
 * panel next to the chat (wide screens), with a close button.
 */
// `activity`: what the bot does in this chat (pages/app/BotPage.jsx), for when the screen
// shows nothing happening: working, waiting for the person, answered, stopped, failed.
const ACTIVITY_PILL = {
  working: ['bg-emerald-500/15 text-emerald-500', 'bots.desktop.pill_working'],
  waiting: ['bg-amber-500/15 text-amber-500', 'bots.desktop.pill_waiting'],
  answered: ['bg-muted text-muted-foreground', 'bots.desktop.pill_answered'],
  stopped: ['bg-amber-500/15 text-amber-500', 'bots.desktop.pill_stopped'],
  failed: ['bg-red-500/15 text-red-500', 'bots.desktop.pill_failed'],
};

// `collapsed` / `onCollapse`: the page keeps whether the inline computer is folded, so the
// bot's cards in the chat know whether its screen shows here (useComputerCollapsed).
const ComputerView = ({ projectId, bot, botName, live = {}, activity, layout = 'inline', onClose, collapsed, onCollapse }) => {
  const { t } = useTranslation();
  const species = useBotSpecies(bot);
  const { data } = useWorkspaceQuery(projectId, {
    refetchInterval: (q) => {
      const st = q.state.data?.workspace?.status;
      return st === 'creating' || q.state.data?.workspace?.waking ? 2000 : st === 'running' ? 5000 : 20000;
    },
  });
  const act = useWorkspaceAction();
  const [big, setBig] = useState(false);
  const [full, setFull] = useState(false);
  const rootRef = useRef(null);
  const [note, setNote] = useState('');
  const [files, setFiles] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  // Full screen: the browser's own where it allows it (asked right in the click, or Firefox
  // refuses), else the computer covers the whole window (iPhone Safari, some app webviews).
  useEffect(() => {
    if (!full) return undefined;
    const onChange = () => { if (!document.fullscreenElement) setFull(false); };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [full]);
  const enterFull = () => {
    setBig(false);
    setFull(true);
    rootRef.current?.requestFullscreen?.().catch(() => { /* not allowed here: the window-filling view stays */ });
  };
  const exitFull = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    setFull(false);
  };

  // The newest desktop action of any of the bot's live runs.
  const action = useMemo(() => Object.values(live)
    .map((l) => l.action).filter((a) => a && (['computer', 'shell'].includes(a.tool) || a.tool?.startsWith('browser_')))
    .sort((a, b) => b.at - a.at)[0], [live]);
  const working = Object.values(live).some((l) => l.status === 'running' || l.tool);

  if (!data?.configured || !data.available) return null;
  const ws = data.workspace;
  const status = ws?.status && ws.status !== 'destroyed' ? ws.status : 'off';
  const running = status === 'running' && ws.preview_url;
  const mine = running && ws.lease.mine;
  // Waking up takes a few seconds: from the first moment (the bot reached for it, or Wake up was
  // pressed) it says so, with the bee waking, instead of asleep with a Wake up button.
  const waking = !running && status !== 'creating' && (!!ws?.waking || act.isPending || (working && !!action));
  const run = (actionName, extra = {}) => act.mutate({ projectId, action: actionName, ...extra }, {
    onSuccess: () => { if (actionName === 'giveBack') setNote(''); },
    onError: (err) => {
      if (err?.response?.status === 402) return;
      const detail = err?.response?.data?.detail;
      // All the computers the plan runs at once are busy: say which, so one can be put to sleep.
      if (detail?.error === 'computers_busy') toast.error(t('bots.desktop.busy', busyComputers(t, detail)), { duration: 12000 });
      else if (detail?.error === 'computer_in_use') toast.error(t('bots.desktop.remove_in_use'));
      else toast.error(detailToMessage(detail) || t('bots.desktop.failed'));
    },
  });
  const toggle = () => onCollapse?.(!collapsed);

  const pill = mine ? { tone: 'bg-amber-500/15 text-amber-500', text: t('bots.desktop.pill_you') }
    : running && ws.lease.owner === 'user' ? { tone: 'bg-amber-500/15 text-amber-500', text: t('bots.desktop.pill_other') }
      : running && working && action ? { tone: 'bg-emerald-500/15 text-emerald-500', text: action.tool === 'shell' ? t('bots.chat.tools.shell') : describeAction(t, action) }
        : running && (ACTIVITY_PILL[activity] || working)
          ? { tone: (ACTIVITY_PILL[activity] || ACTIVITY_PILL.working)[0], text: t((ACTIVITY_PILL[activity] || ACTIVITY_PILL.working)[1]) }
        : running ? { tone: 'bg-muted text-muted-foreground', text: t('bots.desktop.pill_idle') }
          : status === 'creating' ? { tone: 'bg-muted text-muted-foreground', text: t('bots.desktop.starting_long') }
            : waking ? { tone: 'bg-amber-500/15 text-amber-600 dark:text-amber-400', text: t('bots.desktop.pill_waking') }
            : { tone: 'bg-muted text-muted-foreground', text: t('bots.desktop.pill_sleeping') };

  const panel = layout === 'panel';
  const body = (size) => (
    <div className="space-y-2">
      {running ? (
        <Screen ws={ws} action={action} size={size} busy={act.isPending} t={t} onTakeOver={() => run('takeOver')} />
      ) : waking ? (
        <div className={`${panel ? 'h-64' : 'h-32'} rounded-xl bg-gradient-to-b from-amber-500/10 to-transparent ring-1 ring-amber-500/25 flex flex-col items-center justify-center gap-2 text-center px-4`}
          data-testid="computer-waking">
          <Bee species={species} color={species === 'kralovna' ? HONEY : bot?.avatar_color} state="idle" size={panel ? 44 : 34} tight />
          <p className="text-xs text-foreground/80">{t('bots.desktop.waking', { bot: botName })}</p>
        </div>
      ) : status === 'creating' ? (
        <div className={`${panel ? 'h-64' : 'h-32'} rounded-xl bg-muted/40 ring-1 ring-border flex items-center justify-center gap-2 text-sm text-muted-foreground`}>
          <Loader2 className="w-4 h-4 animate-spin" />{t('bots.desktop.starting_long')}
        </div>
      ) : (
        <div className={`${panel ? 'h-64' : 'h-32'} rounded-xl bg-gradient-to-b from-muted/40 to-transparent ring-1 ring-border flex flex-col items-center justify-center gap-2 text-center px-4`}>
          <Moon className="w-5 h-5 text-muted-foreground" />
          <p className="text-xs text-muted-foreground">{t('bots.desktop.sleeping', { bot: botName })}</p>
          <Button size="sm" variant="outline" className="h-7" onClick={() => run('start')}>{t('bots.desktop.wake')}</Button>
        </div>
      )}
      {mine && (
        <form className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2"
          onSubmit={(e) => { e.preventDefault(); run('giveBack', { note }); }}>
          <span className="text-xs text-amber-600 dark:text-amber-400 w-full sm:w-auto sm:flex-1">
            {t('bots.desktop.in_control', { bot: botName })}
          </span>
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000}
            placeholder={t('bots.desktop.note_placeholder')}
            className="flex-1 min-w-[160px] text-sm rounded-md border border-border bg-background px-2 py-1 focus:outline-none focus:border-amber-500/60" />
          <Button type="submit" size="sm" className="h-7 gap-1 bg-amber-500 text-black hover:bg-amber-400" disabled={act.isPending}>
            <Undo2 className="w-3.5 h-3.5" />{t('bots.desktop.hand_back')}
          </Button>
        </form>
      )}
    </div>
  );

  const header = (
    <div className="flex items-center gap-2 min-w-0">
      <span className="relative">
        <BotAvatar bot={bot} size="sm" />
        <Monitor className="absolute -bottom-1 -right-1 w-3 h-3 rounded bg-background text-muted-foreground" />
      </span>
      {/* The name stays readable in a narrow panel with all its buttons; the state pill gives way
          (its whole text is its tooltip): they overlapped. */}
      <div className="min-w-[4.5rem] flex-1">
        <div className="text-sm font-medium leading-tight truncate">{t('bots.desktop.computer_title')}</div>
        {running && ws.title && (
          <div className="flex items-center gap-1 text-[11px] text-muted-foreground truncate">
            <Globe className="w-3 h-3 shrink-0" /><span className="truncate">{ws.title}</span>
          </div>
        )}
      </div>
      <span title={pill.text} className={`min-w-0 max-w-[45%] truncate rounded-full px-2 py-0.5 text-[11px] font-medium ${pill.tone}`}>
        {pill.text}
      </span>
    </div>
  );

  return (
    <div ref={rootRef} data-testid="desktop-panel"
      className={full ? 'fixed inset-0 z-[60] overflow-y-auto bg-background p-3'
        : panel ? 'h-full overflow-y-auto bg-background p-4' : 'border-b border-border bg-background px-4 py-2.5'}>
      <div className="flex items-center gap-1.5">
        <div className="flex-1 min-w-0">{header}</div>
        {ws && status !== 'off' && (
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title={t('bots.desktop.files')} onClick={() => setFiles(true)}
            data-testid="computer-files">
            <FolderOpen className="w-3.5 h-3.5" />
          </Button>
        )}
        {ws && (status === 'running' || status === 'paused') && (
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title={t('bots.desktop.remove')}
            disabled={act.isPending} onClick={() => setConfirmRemove(true)} data-testid="computer-remove">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
        {running && (
          <>
            {!full && (
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title={t('bots.desktop.expand')} onClick={() => setBig(true)}>
                <Maximize2 className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid="computer-fullscreen"
              title={t(full ? 'bots.desktop.exit_fullscreen' : 'bots.desktop.fullscreen')} onClick={full ? exitFull : enterFull}>
              {full ? <Minimize className="w-3.5 h-3.5" /> : <Fullscreen className="w-3.5 h-3.5" />}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title={t('bots.desktop.pause')}
              disabled={act.isPending} onClick={() => run('pause')}>
              <Pause className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
        {full ? null : panel ? onClose && (
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title={t('common.close')} onClick={onClose}
            data-testid="computer-close">
            <X className="w-3.5 h-3.5" />
          </Button>
        ) : (
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title={t(collapsed ? 'bots.desktop.expand' : 'bots.desktop.collapse')}
            onClick={toggle} data-testid="computer-toggle">
            {collapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
          </Button>
        )}
      </div>
      {full ? <div className="pt-3">{body('full')}</div>
        : (panel || !collapsed) && !big && <div className={panel ? 'pt-4' : 'pt-2.5'}>{body(panel ? 'panel' : 'small')}</div>}
      {files && <WorkspaceFiles projectId={projectId} open onClose={() => setFiles(false)} />}
      <ConfirmModal isOpen={confirmRemove} onClose={() => setConfirmRemove(false)} danger
        onConfirm={() => { setConfirmRemove(false); run('remove'); }} title={t('bots.desktop.remove_title')}
        message={t('bots.desktop.remove_message')} confirmLabel={t('bots.desktop.remove')} />
      <Dialog open={big} onOpenChange={setBig}>
        <DialogContent className="max-w-6xl w-[95vw] p-4 gap-3">
          <DialogTitle className="sr-only">{t('bots.desktop.computer_title')}</DialogTitle>
          <DialogDescription className="sr-only">{ws?.title || ''}</DialogDescription>
          <div className="pr-8">{header}</div>
          {body('big')}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComputerView;
